import React from "react";
import { Loader2 } from "lucide-react";
import { BORDER_SMART, THEME_BLUE_BG } from "./configHelpers";

export function Card({ title, icon: Icon, rightAction, children }) {
  return (
    <div
      className={`rounded-xl bg-white p-2.5 shadow-sm border ${BORDER_SMART}`}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          {Icon ? (
            <div
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border ${BORDER_SMART} ${THEME_BLUE_BG} text-[#01C37D]`}
            >
              <Icon size={12} />
            </div>
          ) : null}

          <div className="truncate text-[10px] font-bold uppercase tracking-wider text-gray-400">
            {title}
          </div>
        </div>

        {rightAction}
      </div>

      <div>{children}</div>
    </div>
  );
}

export function FormSection({ title, description, icon: Icon, children }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-[#fbfdff] p-2">
      <div className="mb-1.5 flex items-center gap-1.5">
        {Icon ? (
          <div className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-white text-[#01C37D] shadow-sm">
            <Icon size={12} />
          </div>
        ) : null}

        <div className="min-w-0">
          <h3 className="truncate text-[11px] font-bold leading-none text-gray-900">
            {title}
          </h3>

          {description ? (
            <p className="mt-0.5 truncate text-[9px] leading-none text-gray-500">
              {description}
            </p>
          ) : null}
        </div>
      </div>

      {children}
    </section>
  );
}

export function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      <label className="ml-0.5 text-[10px] font-semibold text-gray-500">
        {label}
      </label>
      {children}
    </div>
  );
}

export function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`h-8 w-full rounded-md border ${BORDER_SMART} bg-white px-2 text-[11px] font-medium outline-none transition-all
                  focus:border-[#01C37D] focus:ring-1 focus:ring-[#01C37D]/10 ${className}`}
    />
  );
}

export function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full border transition-colors
        ${
          checked
            ? "bg-[#01C37D] border-[#00ab6d]"
            : "bg-gray-300 border-gray-400"
        }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform
          ${checked ? "translate-x-4.5" : "translate-x-0.5"}`}
      />
    </button>
  );
}

export function CapabilityBox({ active, label, icon: Icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[34px] items-center justify-center gap-1.5 rounded-md border px-2 transition-all
        ${
          active
            ? "border-[#01C37D] bg-emerald-50/60 text-[#01C37D]"
            : `border-gray-200 ${THEME_BLUE_BG} text-gray-400 hover:border-gray-300 hover:bg-white`
        }`}
    >
      <Icon size={13} strokeWidth={active ? 2.5 : 1.8} />
      <span className="truncate text-[10px] font-bold uppercase tracking-tight">
        {label}
      </span>
    </button>
  );
}

export function InfoBox({ label, value }) {
  return (
    <div className="rounded-lg border bg-white px-2.5 py-2">
      <div className="text-[10px] font-semibold text-gray-500">{label}</div>
      <div className="mt-0.5 break-all text-[11px] font-bold text-gray-900">
        {value}
      </div>
    </div>
  );
}

export function ConfigSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
      <div className="xl:col-span-8">
        <div
          className={`animate-pulse rounded-xl border ${BORDER_SMART} bg-white p-2.5`}
        >
          <div className="mb-3 h-4 w-36 rounded bg-gray-200" />

          <div className="space-y-2">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="rounded-lg border border-gray-200 bg-[#fbfdff] p-2"
              >
                <div className="mb-2 h-3 w-32 rounded bg-gray-200" />

                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <div className="h-8 rounded-md bg-gray-200" />
                  <div className="h-8 rounded-md bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="xl:col-span-4">
        <div
          className={`animate-pulse rounded-xl border ${BORDER_SMART} bg-white p-2.5`}
        >
          <div className="mb-3 h-4 w-28 rounded bg-gray-200" />

          <div className="grid grid-cols-2 gap-2">
            <div className="h-12 rounded-lg bg-gray-200" />
            <div className="h-12 rounded-lg bg-gray-200" />
            <div className="h-12 rounded-lg bg-gray-200" />
            <div className="h-12 rounded-lg bg-gray-200" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function GstCardSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="h-12 rounded-lg bg-gray-200" />
        <div className="h-12 rounded-lg bg-gray-200" />
        <div className="h-12 rounded-lg bg-gray-200" />
        <div className="h-12 rounded-lg bg-gray-200" />
      </div>

      <div className="mt-2 h-9 rounded-lg bg-gray-200" />
    </div>
  );
}

export function SyncPill() {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-[#01C37D]">
      <Loader2 size={11} className="animate-spin" />
      Syncing saved configuration
    </div>
  );
}
